import restApi from "@common/utils/api/restApi";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import PropTypes from "prop-types";
import SocialLogin from "@common/enums/SocialLogin.enums";
import React, {useContext, useLayoutEffect, useEffect, useState} from "react";
import {
  getAuth,
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  getRedirectResult
} from 'firebase/auth';
import {fireBaseAuth} from '../../utils/auth/Firebase';

const defaultValue = {};
const AuthContext = React.createContext(defaultValue);

const DEFAULT_APP_ID = "app";
const DEFAULT_ACCESS_TOKEN_KEY = "accessToken";
const LOGIN_SOCIAL_PROVIDER_KEY = "socialProvider";
const LOGIN_AFTER_REDIRECT_URL_KEY = "loginAfterRedirectUrl";
import LoadingView from "@common/components/loading/LoadingView";
import {isMobileApp} from "@common/utils/app/appUtils";
import {registerPushDevice} from "@common/utils/app/appBridge";

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within a AuthContext");
  }
  return context;
}

const isDevelop = process.env.PROFILE !== 'production';
const socialProvider = [SocialLogin.apple, SocialLogin.google, SocialLogin.kakao];
const fireBaseProvider = [SocialLogin.apple.enumKey, SocialLogin.google.enumKey];

function AuthProvider({
                        children,
                        appId = DEFAULT_APP_ID,
                        accessTokenKey = DEFAULT_ACCESS_TOKEN_KEY,
                        socialProviderKey = LOGIN_SOCIAL_PROVIDER_KEY,
                        loginAfterRedirectKey = LOGIN_AFTER_REDIRECT_URL_KEY,
                        redirectAfterSignIn = () => {
                        },
                        redirectToSignIn = () => {
                        },
                        onCheckAuth = () => {
                        },
                        onCheckAuthSuccess = () => {
                        },
                        onCheckAuthFailed = () => {
                        },
                        onSignIn = () => {
                        },
                        onSignInSuccess = () => {
                        },
                        onSignInFailed = () => {
                        },
                        onSignOutSuccess = () => {
                        },
                        onSignOut = () => {
                        },
                        onForbiddenReq = () => {
                        },
                        onUserDataFetch = () => {
                        },
                      }) {
  const [adicUser, setAdicUser] = useState(null);
  const [userProvider, setUserProvider] = useState(localStorage.getItem(socialProviderKey));
  const [initializing, setInitializing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);

  useLayoutEffect(() => {
    if (isMobileApp() || isDevelop) {
      onAuthStateChanged(fireBaseAuth, async (user) => {
        if (user && fireBaseProvider.includes(userProvider)) {
          async function signOut(callback) {
            clearApiAuthorization();
            await signOut(fireBaseAuth).then(() => {
              // Sign-out successful.
              if (typeof "function" === callback) {
                callback();
              }
            }).catch((error) => {
              // An error happened.
            })
          }

          Cookies.set(appId, "LOGGED_IN");
          const providerUser = {
            userId: user.auth.currentUser.uid,
            userEmail: user.auth.currentUser.email,
            socialType: userProvider
          };
          const token = getApiAuthorization();
          if (!token) {
            try {
              const token = await onSignIn(userProvider, user.accessToken);
              if (!token || !token.accessToken) {
                await signOut(onSignInFailed);
                return;
              }
              const accessToken = token.accessToken;
              const claims = jwtDecode(accessToken);
              if (!claims) {
                await signOut(onSignInFailed);
                return;
              }
              const expInMs = claims.exp * 1000;
              const expDate = new Date(expInMs);
              const nowInMs = Date.now();
              if (expInMs < nowInMs) {
                await signOut(onSignInFailed);
                return;
              }

              storeApiAuthorization(accessToken);
              configApiAuthorization(accessToken);
              if (!user.auth.currentUser) {
                await signOut(onSignInFailed);
                return;
              }
              if (isMobileApp()) {
                await registerPushDevice();
              }
              localStorage.setItem(accessTokenKey, accessToken);
              setAdicUser(providerUser);
              onSignInSuccess({adicUser: providerUser, expDate, claims});
              setAuthenticated(true);
              redirectAfterSignIn(localStorage.getItem(loginAfterRedirectKey));
            }
            finally {
              setInitializing(false);
            }
          } else {
            const claims = jwtDecode(token);
            if (!claims) {
              await signOut(onCheckAuthFailed);
              return;
            }

            const expInMs = claims.exp * 1000;
            const expDate = new Date(expInMs);
            const nowInMs = Date.now();
            if (expInMs < nowInMs) {
              await signOut(onCheckAuthFailed);
              return;
            }
            configApiAuthorization(token);
            const data = await onUserDataFetch(userProvider);
            if (!data) {
              await signOut(onCheckAuthFailed);
              return;
            }
            setAuthenticated(true);
            setInitializing(false);
            setAdicUser(providerUser);
            onCheckAuthSuccess({adicUser: providerUser, expDate, claims});
          }
        } else {
          // User is signed out
          if (fireBaseProvider.includes(userProvider)) {
            clearApiAuthorization();
            setAdicUser(null);
            setUserProvider(null);
            setAuthenticated(false);
            setInitializing(false);
          }
        }
      });
    }
  }, []);

  useLayoutEffect(() => {
    if (isMobileApp() || isDevelop) {
      (async () => {
        try {
          if (userProvider && !fireBaseProvider.includes(userProvider)) {
            const code = new URL(window.location.href).searchParams.get("code");
            let accessToken = null;
            if (code) {
              const token = await onSignIn(userProvider, code, process.env.KAKAO_LOGIN_REDIRECT_URL);
              if (!token || !token.accessToken) {
                onSignInFailed();
                return;
              }
              accessToken = token.accessToken;
            } else {
              accessToken = getApiAuthorization();
              if (!accessToken) {
                onCheckAuthFailed();
                return;
              }
            }
            const claims = jwtDecode(accessToken);
            if (!claims) {
              if (code) {
                onSignInFailed();
              } else {
                onCheckAuthFailed();
              }
              return;
            }

            const expInMs = claims.exp * 1000;
            const expDate = new Date(expInMs);
            const nowInMs = Date.now();
            if (expInMs < nowInMs) {
              if (code) {
                onSignInFailed();
              } else {
                onCheckAuthFailed();
              }
              return;
            }
            storeApiAuthorization(accessToken);
            configApiAuthorization(accessToken);
            const data = await onUserDataFetch(userProvider);
            if (!data) {
              clearApiAuthorization();
              if (code) {
                onSignInFailed();
              } else {
                onCheckAuthFailed();
              }
              return;
            }
            const adicUser = Object.assign(data, {socialType: userProvider});
            setAdicUser(adicUser);
            if (code) {
              if (isMobileApp()) {
                await registerPushDevice();
              }
              onSignInSuccess({adicUser: adicUser, expDate, claims});
              redirectAfterSignIn(localStorage.getItem(loginAfterRedirectKey));
            }
            onCheckAuthSuccess({adicUser: adicUser, expDate, claims});
            setAuthenticated(true);
          }
        } finally {
          if (!fireBaseProvider.includes(userProvider)) {
            setInitializing(false);
          }
        }
      })();
    } else if (!isDevelop) {
      const accessToken = getApiAuthorization();
      if (accessToken) {
        (async () => {
          await handleSignOut();
          setInitializing(false);
        })();
      }
    }
  }, []);

  const handleSignIn = async (provider) => {
    try {
      const socialProviderKeys = socialProvider.map(social => social.enumKey);
      if (!socialProviderKeys.includes(provider)) {
        throw new Error("지원하지 않는 로그인 Provider 입니다.");
      }
      setAuthenticated(false);
      setLoading(true);
      setInitializing(true);
      setUserProvider(provider);
      localStorage.setItem(socialProviderKey, provider);
      localStorage.setItem(loginAfterRedirectKey, document.referrer);

      if (fireBaseProvider.includes(provider)) {
        const fireBaseProvider = getFireBaseProvider(provider);
        await signInWithRedirect(fireBaseAuth, fireBaseProvider);
      } else if (SocialLogin.kakao.enumKey === provider) {
        handleKakaoLogin();
      }
    } finally {
      setLoading(false);
    }
  };

  const storeApiAuthorization = (accessToken) => {
    localStorage.setItem(accessTokenKey, accessToken);
    Cookies.set(appId, "LOGGED_IN");
  };

  const configApiAuthorization = (accessToken) => {
    restApi.defaults.headers.common["Authorization"] = "Bearer " + (accessToken || "");
  };

  const getApiAuthorization = (isClear) => {
    const appState = Cookies.get(appId);
    if (appState !== "LOGGED_IN") {
      clearApiAuthorization();
      return null;
    }
    return localStorage.getItem(accessTokenKey);
  };

  const clearApiAuthorization = () => {
    if (restApi.defaults.headers.common) {
      delete restApi.defaults.headers.common["Authorization"];
    }
    localStorage.removeItem(accessTokenKey);
    localStorage.removeItem(socialProviderKey);
    localStorage.removeItem(loginAfterRedirectKey);
    Cookies.remove(appId);
  };


  const getFireBaseProvider = (fromProvider) => {
    const fireBaseProvider = socialProvider.find(social => social.enumKey === fromProvider);
    if (!fireBaseProvider) return null;
    const provider = new OAuthProvider(fireBaseProvider.provider);
    provider.addScope('email');
    provider.setCustomParameters({
      locale: 'ko_KR'
    });
    return provider;
  };

  const handleKakaoLogin = () => {
    location.href = `${process.env.KAKAO_AUTH_URL}?client_id=${process.env.REACT_APP_KAKAO_ID}&redirect_uri=${process.env.KAKAO_LOGIN_REDIRECT_URL}&response_type=code`;
  };

  const refetchUser = async () => {
    try {
      setLoading(true);
      const data = await onUserDataFetch(userProvider);
      if (!data) {
        throw new Error("Failed to refetch user data.");
      }
      setAdicUser(data);
    } finally {
      setLoading(false);
    }
  };

  const signOutSuccess = () => {
    clearApiAuthorization();
    setAdicUser(null);
    setAuthenticated(false);
    setUserProvider(null);
    onSignOutSuccess();
  };

  const handleSignOut = async () => {
    if (fireBaseProvider.includes(userProvider)) {
      await signOut(fireBaseAuth).then(() => {
        // Sign-out successful.
        signOutSuccess();
      }).catch((error) => {
        // An error happened.
        alert("로그아웃을 실패하였습니다. 잠시후 재시도 해보시기 바랍니다. : " + error);
      })
    } else if (SocialLogin.kakao.enumKey === userProvider) {
      const resultSignOut = await onSignOut(userProvider);
      if (resultSignOut) {
        signOutSuccess();
      }
    }
  };
  // console.log("user @@@@@@@@@@@@@@@@@@@@@@@@ ",adicUser);
  // console.log("initializing >>>>  ",initializing);
  // console.log("authenticated >>>>  ",authenticated);

  const auth = {
    redirectAfterSignIn,
    redirectToSignIn,
    adicUser,
    socialProvider,
    initializing,
    loading,
    authenticated,
    refetchUser,
    signIn: handleSignIn,
    signOut: handleSignOut,
  };

  if ((isMobileApp() || isDevelop) && initializing) return <LoadingView/>;

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
  appId: PropTypes.string,
  accessTokenKey: PropTypes.string,
  redirectAfterSignIn: PropTypes.func,
  redirectToSignIn: PropTypes.func,
  onCheckAuth: PropTypes.func,
  onCheckAuthSuccess: PropTypes.func,
  onCheckAuthFailed: PropTypes.func,
  onSignIn: PropTypes.func,
  onSignInSuccess: PropTypes.func,
  onSignOut: PropTypes.func,
  onForbiddenReq: PropTypes.func,
  onUserDataFetch: PropTypes.func,
};

export default AuthProvider;
export {AuthContext, useAuth};
